import { useEffect, useState } from "react";

import { isAPIError } from "@/core/services";
import { periodoDaObra } from "./equipe-obra.rules";
import { listAllPapeisObra } from "./papeis-obra.gateway";
import type { PapelObra } from "./papeis-obra.types";
import { listAllProfissionais } from "./profissionais.gateway";
import type { Profissional } from "./profissionais.types";

export const EQUIPE_OBRA_OPCOES_ERROR_MESSAGE = "Não foi possível carregar os profissionais.";
export const EQUIPE_OBRA_PAPEIS_ERROR_MESSAGE = "Não foi possível carregar os papéis na obra.";

export interface EquipeObraOpcoesState {
  status: "loading" | "success" | "error";
  papeis: PapelObra[];
  profissionais: Profissional[];
  /** Falhas independentes: papéis e profissionais carregam em paralelo. */
  papeisError: string | null;
  profissionaisError: string | null;
  forbidden: boolean;
}

const INITIAL: EquipeObraOpcoesState = {
  status: "loading",
  papeis: [],
  profissionais: [],
  papeisError: null,
  profissionaisError: null,
  forbidden: false,
};

/**
 * Opções do modal Definir Equipe de Obra: papéis ativos e profissionais disponíveis
 * no período (meses) da obra.
 */
export function useEquipeObraOpcoes(inicio: string, fim: string): EquipeObraOpcoesState {
  const [state, setState] = useState<EquipeObraOpcoesState>(INITIAL);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setState(INITIAL);

    Promise.allSettled([
      listAllPapeisObra(controller.signal),
      listAllProfissionais(periodoDaObra(inicio, fim), controller.signal),
    ]).then(([papeis, profissionais]) => {
      if (cancelled) return;

      const forbidden = [papeis, profissionais].some(
        (result) =>
          result.status === "rejected" && isAPIError(result.reason) && result.reason.status === 403,
      );

      setState({
        status:
          papeis.status === "rejected" || profissionais.status === "rejected" ? "error" : "success",
        papeis: papeis.status === "fulfilled" ? papeis.value : [],
        profissionais: profissionais.status === "fulfilled" ? profissionais.value : [],
        papeisError: papeis.status === "rejected" ? EQUIPE_OBRA_PAPEIS_ERROR_MESSAGE : null,
        profissionaisError:
          profissionais.status === "rejected" ? EQUIPE_OBRA_OPCOES_ERROR_MESSAGE : null,
        forbidden,
      });
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [inicio, fim]);

  return state;
}
